// Fichas de árbitros: PDF con una ficha por partido, 6 por hoja A4 (2 columnas x
// 3 filas), para recortar y repartir. El árbitro escribe arriba su nombre y anota
// a mano los goles de cada equipo y las tarjetas (roja, amarilla, verde) que sacó
// durante el partido.
//
// Cada ficha lleva impresos la zona, la cancha, el horario y los dos equipos; el
// resto va en blanco. Los partidos dados por walk-over no llevan ficha: no se
// juegan, así que no hay nada para arbitrar ni para anotar.
//
// Después de las fichas de los partidos se agregan en blanco (mismo formato, sin
// datos) las que haga falta para que la última hoja salga completa: sirven de
// repuesto si alguna se pierde o se arruina, y de paso no se imprime media hoja.
//
// jspdf se carga con import() dinámico para que no entre en el bundle principal
// de la PWA: solo se baja cuando se toca el botón.

import { nombreDeCancha, claveDeCancha, porCanchaYHora } from "./horarios";
import { esWO } from "./resultado";

// El logo de la app (el mismo del ícono de la PWA). Es un PNG cuadrado con aire
// alrededor: el arte ocupa el 84.8% del ancho y va del 23.4% al 76.2% del alto,
// centrado. Para que el arte quede del tamaño que se quiere, la imagen se dibuja
// más grande (el aire cae fuera de la caja, pero es transparente y no tapa nada).
const LOGO = process.env.PUBLIC_URL + "/images/icon_512x512.png";
const LOGO_AIRE = 0.848;
const LOGO_ALTO_DEL_ARTE = 0.528;

// Hoja A4 en milímetros y grilla de 2 x 3 fichas.
const COLUMNAS = 2;
const FILAS = 3;
export const FICHAS_POR_HOJA = COLUMNAS * FILAS;
const MARGEN = 8;
const SEPARACION = 5;

// Reparto interno de la ficha, en milímetros.
const COL_IZQUIERDA = 19; // franja del logo, el rótulo "EQUIPOS" y las tarjetas
const PAD = 2.5;
const ALTO_TARJETA = 12.5; // cada una de las tres filas de tarjetas
const BANDA_DATOS = 8; // zona / cancha / hora
const DATOS_CUERPO = 10.5; // cuerpo máximo de esa banda; se achica si el texto no entra
const DATOS_SEPARACION = 3; // aire mínimo entre la zona y la cancha, que van una a cada punta
// Alturas dentro del bloque de arriba, medidas desde el borde superior de la ficha.
const CAJA_GOLES = 14; // lado del casillero donde va la cantidad de goles
const CAJA_GOLES_DESDE = 17.5;
const GOLES_CUERPO = 13; // la palabra "GOLES", centrada con los casilleros
const DIVISOR_DESDE = 34; // la línea que parte las dos columnas de equipos
const LOGO_ANCHO_DEL_ARTE = 11;
const LOGO_DESDE_ARRIBA = 5.5; // el arte arranca acá, para no comerse el marco

// Nombre del equipo: hasta NOMBRE_RENGLONES renglones; recién si con eso no entra
// se achica la letra.
const NOMBRE_CUERPO_MAXIMO = 10.5;
const NOMBRE_CUERPO_MINIMO = 7;
const NOMBRE_RENGLONES = 2;

// jsPDF mide las fuentes en puntos y la hoja en milímetros.
const MM_POR_PUNTO = 0.3528;
// Alto de una mayúscula de Helvetica respecto del cuerpo de letra (cap height).
// Con esto un texto se centra por su alto real y no por la caja de la fuente, que
// lleva aire arriba y abajo.
const ALTO_DE_MAYUSCULA = 0.717;

// Alto real de un texto en mayúsculas y baseline para que quede centrado en `centroY`.
const altoDeMayuscula = (cuerpo) => cuerpo * ALTO_DE_MAYUSCULA * MM_POR_PUNTO;
const baseCentrada = (centroY, cuerpo) => centroY + altoDeMayuscula(cuerpo) / 2;

const VERDE = [0, 138, 68];
const ROJO = [211, 39, 45];
const AMARILLO = [252, 233, 0];
const GRIS = [140, 140, 140];

const limpiarNombreArchivo = (texto) => texto.replace(/[\\/:*?"<>|]/g, "-").replace(/\s+/g, " ").trim();

// El logo se baja y se pasa como data URI. Si en vez de eso se le da a jsPDF un
// <img> con una URL, lo resuelve con un XHR sincrónico que traba la pantalla.
const cargarLogo = async () => {
  const respuesta = await fetch(LOGO);
  const blob = await respuesta.blob();
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result);
    lector.onerror = reject;
    lector.readAsDataURL(blob);
  });
};

// Los partidos de la jornada que llevan ficha: todos menos los que se dieron por
// walk-over. Van agrupados por zona y, dentro de cada zona, por cancha y horario,
// que es el orden en el que se reparten. Lo usan la ficha y el modal que la
// ofrece, para que lo que se anuncia sea lo que termina saliendo en el PDF.
export function partidosParaFichas(fechas = []) {
  return fechas.flatMap(({ zone, matchDate }) =>
    ((matchDate && matchDate.matches) || [])
      .filter((m) => !esWO(m))
      .sort(porCanchaYHora)
      .map((m) => ({
        zona: (zone && zone.name) || "",
        cancha: m.cancha ? nombreDeCancha(claveDeCancha(m)) : "",
        hora: m.hora || "",
        homeTeamId: m.homeTeamId,
        awayTeamId: m.awayTeamId,
      }))
  );
}

// Cuántas fichas en blanco se agregan: las que faltan para completar la hoja.
export function fichasEnBlanco(cantidadDePartidos) {
  const sobrantes = cantidadDePartidos % FICHAS_POR_HOJA;
  return sobrantes === 0 ? 0 : FICHAS_POR_HOJA - sobrantes;
}

// fechas: [{ zone, matchDate, numero }] — las fechas de la jornada, una por zona,
// tal como las devuelve `jornadasDeRueda`. numero / dia / torneoNombre / torneoAno
// solo se usan para el nombre del archivo.
// teams: equipos del torneo.
export async function generarFichasArbitros({
  fechas = [],
  numero,
  dia,
  torneoNombre,
  torneoAno,
  teams = [],
}) {
  const partidos = partidosParaFichas(fechas);

  const nombreDeEquipo = (teamId) => {
    const t = teams.find((x) => x.id === teamId);
    return t ? t.name : "";
  };

  const fichas = partidos.map((p) => ({
    zona: p.zona,
    cancha: p.cancha,
    hora: p.hora,
    local: nombreDeEquipo(p.homeTeamId),
    visitante: nombreDeEquipo(p.awayTeamId),
  }));

  const enBlanco = fichasEnBlanco(fichas.length);
  for (let i = 0; i < enBlanco; i++) fichas.push({ zona: "", cancha: "", hora: "", local: "", visitante: "" });

  const [{ jsPDF }, logo] = await Promise.all([
    import("jspdf"),
    // Si el logo no carga (sin conexión y sin cachear), la ficha sale igual sin él.
    cargarLogo().catch(() => null),
  ]);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const anchoPagina = doc.internal.pageSize.getWidth();
  const altoPagina = doc.internal.pageSize.getHeight();
  const anchoFicha = (anchoPagina - MARGEN * 2 - SEPARACION * (COLUMNAS - 1)) / COLUMNAS;
  const altoFicha = (altoPagina - MARGEN * 2 - SEPARACION * (FILAS - 1)) / FILAS;

  // Cuerpo de letra más grande (hasta `maximo`) con el que el texto entra en
  // `ancho`, sin bajar de `minimo`.
  const cuerpoQueEntra = (texto, ancho, maximo, minimo, estilo = "normal") => {
    doc.setFont("helvetica", estilo);
    let cuerpo = maximo;
    while (cuerpo > minimo) {
      doc.setFontSize(cuerpo);
      if (doc.getTextWidth(texto) <= ancho) break;
      cuerpo -= 0.25;
    }
    doc.setFontSize(cuerpo);
    return cuerpo;
  };

  const escribir = (texto, x, y, ancho, maximo, { align = "left", estilo = "normal", minimo = 5 } = {}) => {
    if (!texto) return;
    cuerpoQueEntra(texto, ancho, maximo, minimo, estilo);
    doc.setTextColor(0, 0, 0);
    doc.text(texto, x, y, { align });
  };

  // El nombre del equipo, centrado en su columna. Antes que achicar la letra hasta
  // que no se lea, se parte en dos renglones: los nombres largos ("CLUB ATLETICO
  // SAN ISIDRO ROJO") son la norma, no la excepción.
  const escribirEquipo = (texto, x, centroY, ancho) => {
    if (!texto) return;
    doc.setFont("helvetica", "bold");
    let cuerpo = NOMBRE_CUERPO_MAXIMO;
    let renglones = [texto];
    while (cuerpo > NOMBRE_CUERPO_MINIMO) {
      doc.setFontSize(cuerpo);
      renglones = doc.splitTextToSize(texto, ancho);
      if (renglones.length <= NOMBRE_RENGLONES) break;
      cuerpo -= 0.25;
    }
    doc.setFontSize(cuerpo);
    doc.setTextColor(0, 0, 0);

    // El bloque entero (uno o dos renglones) queda centrado en `centroY`.
    const usados = renglones.slice(0, NOMBRE_RENGLONES);
    const interlineado = cuerpo * MM_POR_PUNTO * 1.2;
    const primera = baseCentrada(centroY - (interlineado * (usados.length - 1)) / 2, cuerpo);
    usados.forEach((renglon, i) => doc.text(renglon, x, primera + interlineado * i, { align: "center" }));
  };

  const linea = (x1, y1, x2, y2, grosor, color = [0, 0, 0]) => {
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(grosor);
    doc.line(x1, y1, x2, y2);
  };

  // Las tres tarjetas, en el orden de la ficha original: roja, amarilla, verde.
  const dibujarTarjeta = (indice, cx, cy) => {
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    if (indice === 0) {
      doc.setFillColor(ROJO[0], ROJO[1], ROJO[2]);
      doc.circle(cx, cy, 3.1, "FD");
    } else if (indice === 1) {
      doc.setFillColor(AMARILLO[0], AMARILLO[1], AMARILLO[2]);
      doc.rect(cx - 3, cy - 3, 6, 6, "FD");
    } else {
      doc.setFillColor(VERDE[0], VERDE[1], VERDE[2]);
      doc.triangle(cx, cy - 3.4, cx - 3.8, cy + 3.2, cx + 3.8, cy + 3.2, "FD");
    }
  };

  const dibujarFicha = (ficha, x, y) => {
    const derechaX = x + COL_IZQUIERDA;
    const anchoDerecha = anchoFicha - COL_IZQUIERDA;
    const altoTarjetas = ALTO_TARJETA * 3;
    const bloqueSuperior = altoFicha - altoTarjetas;
    const medioX = derechaX + anchoDerecha / 2;

    // Marco de la ficha.
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.rect(x, y, anchoFicha, altoFicha);

    // Franja izquierda: logo arriba y "EQUIPOS" en vertical debajo.
    if (logo) {
      const lado = LOGO_ANCHO_DEL_ARTE / LOGO_AIRE;
      const centroDelArte = y + LOGO_DESDE_ARRIBA + (LOGO_ANCHO_DEL_ARTE * LOGO_ALTO_DEL_ARTE) / 2;
      doc.addImage(logo, "PNG", x + COL_IZQUIERDA / 2 - lado / 2, centroDelArte - lado / 2, lado, lado);
    }

    const letras = "EQUIPOS".split("");
    const desdeY = y + 14;
    const hastaY = y + bloqueSuperior - 3;
    const paso = (hastaY - desdeY) / letras.length;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    letras.forEach((letra, i) => {
      doc.text(letra, x + COL_IZQUIERDA / 2, desdeY + paso * i + paso / 2, { align: "center" });
    });

    linea(derechaX, y, derechaX, y + bloqueSuperior, 0.9);

    // Banda de datos del partido: zona a la izquierda, cancha y hora a la derecha.
    const canchaYHora = [ficha.cancha, ficha.hora].filter(Boolean).join(" · ");
    const baseDatos = y + BANDA_DATOS - 2.4;
    // La cancha y la hora se acomodan primero (son las que hay que leer de lejos
    // al repartir las fichas) y la zona se queda con lo que sobra: así los dos
    // textos nunca se tocan, por largo que sea el nombre de la zona.
    cuerpoQueEntra(canchaYHora, anchoDerecha / 2 - PAD, DATOS_CUERPO, 5, "bold");
    const anchoCanchaYHora = canchaYHora ? doc.getTextWidth(canchaYHora) : 0;
    escribir(canchaYHora, x + anchoFicha - PAD, baseDatos, anchoDerecha / 2 - PAD, DATOS_CUERPO, {
      align: "right",
      estilo: "bold",
    });
    escribir(
      (ficha.zona || "").toUpperCase(),
      derechaX + PAD,
      baseDatos,
      anchoDerecha - PAD * 2 - anchoCanchaYHora - DATOS_SEPARACION,
      DATOS_CUERPO,
      { estilo: "bold" }
    );
    linea(derechaX, y + BANDA_DATOS, x + anchoFicha, y + BANDA_DATOS, 0.2, GRIS);

    // Rótulo para que el árbitro escriba su nombre.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text("Árbitro:", derechaX + PAD, y + BANDA_DATOS + 5.5);

    // "GOLES" con un casillero a cada lado, uno por equipo, para anotar el
    // resultado; debajo arranca la columna de cada equipo, con su nombre impreso.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(GOLES_CUERPO);
    doc.text("GOLES", medioX, baseCentrada(y + CAJA_GOLES_DESDE + CAJA_GOLES / 2, GOLES_CUERPO), {
      align: "center",
    });

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    [derechaX + anchoDerecha / 4, medioX + anchoDerecha / 4].forEach((centro) => {
      doc.rect(centro - CAJA_GOLES / 2, y + CAJA_GOLES_DESDE, CAJA_GOLES, CAJA_GOLES);
    });

    linea(medioX, y + DIVISOR_DESDE, medioX, y + altoFicha, 0.4);

    // Los nombres van centrados en lo que queda entre el divisor y las tarjetas.
    const anchoNombre = anchoDerecha / 2 - PAD * 2;
    const centroNombre = y + (DIVISOR_DESDE + bloqueSuperior) / 2;
    escribirEquipo(ficha.local, derechaX + anchoDerecha / 4, centroNombre, anchoNombre);
    escribirEquipo(ficha.visitante, medioX + anchoDerecha / 4, centroNombre, anchoNombre);

    // Bloque de tarjetas: tres filas, separadas por la doble línea verde.
    linea(x, y + bloqueSuperior, x + anchoFicha, y + bloqueSuperior, 0.7);
    linea(derechaX, y + bloqueSuperior, derechaX, y + altoFicha, 0.3);
    for (let i = 0; i < 3; i++) {
      const filaY = y + bloqueSuperior + ALTO_TARJETA * i;
      if (i > 0) {
        linea(x, filaY - 0.35, x + anchoFicha, filaY - 0.35, 0.5, VERDE);
        linea(x, filaY + 0.35, x + anchoFicha, filaY + 0.35, 0.5, VERDE);
      }
      dibujarTarjeta(i, x + COL_IZQUIERDA / 2, filaY + ALTO_TARJETA / 2);
    }
  };

  fichas.forEach((ficha, indice) => {
    const enHoja = indice % FICHAS_POR_HOJA;
    if (indice > 0 && enHoja === 0) doc.addPage();
    const columna = enHoja % COLUMNAS;
    const fila = Math.floor(enHoja / COLUMNAS);
    dibujarFicha(
      ficha,
      MARGEN + columna * (anchoFicha + SEPARACION),
      MARGEN + fila * (altoFicha + SEPARACION)
    );
  });

  const nombreArchivo = limpiarNombreArchivo(
    [
      "Fichas Arbitros",
      [torneoNombre || "", torneoAno].filter(Boolean).join(" "),
      numero ? "Fecha " + numero : "",
      dia || "",
    ]
      .filter(Boolean)
      .join(" - ")
  );

  doc.save(nombreArchivo + ".pdf");
}
